import { useRef } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS } from "@/shared/constants/taskIds";

export interface WillStepAnswers {
  "has-will"?: "yes" | "no" | "not-sure";
  attorney?: "prepared" | "different" | "none";
  "resume-step"?: string;
  "estate-size-back"?: string;
  "will-confirm"?: Record<string, unknown>;
  "executor-invite"?: Record<string, unknown>;
  "attorney-invite"?: Record<string, unknown>;
  "will-requested-from"?: string;
}

export function useWillStepAnswers(estateId: number | undefined) {
  const queryClient = useQueryClient();

  const { data: savedAnswers, isFetched } = useQuery({
    queryKey: ["willStepAnswers", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_tasks")
        .select("answers")
        .eq("estate_id", estateId!)
        .eq("task_id", TASK_IDS.WILL)
        .maybeSingle();
      return (data?.answers as WillStepAnswers | null) ?? null;
    },
    enabled: !!estateId,
    staleTime: 0,
  });

  const saveAnswers = useMutation({
    mutationFn: async (answers: WillStepAnswers) => {
      if (!estateId) return;
      const { data: existing, error: readError } = await supabase
        .from("estate_tasks")
        .select("status")
        .eq("estate_id", estateId)
        .eq("task_id", TASK_IDS.WILL)
        .maybeSingle();
      if (readError) throw readError;
      const status = existing?.status === "completed" ? "completed" : "in_progress";
      const { error } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: TASK_IDS.WILL,
          status,
          answers,
          last_active_at: new Date().toISOString(),
        },
        { onConflict: "estate_id,task_id" },
      );
      if (error) throw error;
    },
    onMutate: async (answers) => {
      await queryClient.cancelQueries({ queryKey: ["willStepAnswers", estateId] });
      queryClient.setQueryData(["willStepAnswers", estateId], answers);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["willStepAnswers", estateId] });
      queryClient.invalidateQueries({ queryKey: ["probateAnswers", estateId] });
    },
  });

  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const lastSaveError = useRef<unknown>(null);

  const mergeAndSave = (partial: Partial<WillStepAnswers>) => {
    const next = saveQueue.current.then(async () => {
      lastSaveError.current = null;
      const latest = queryClient.getQueryData<WillStepAnswers | null>([
        "willStepAnswers",
        estateId,
      ]);
      const hasWill = partial["has-will"] ?? latest?.["has-will"];
      const merged = {
        ...latest,
        ...partial,
        ...(hasWill ? { "has-will": hasWill } : {}),
      } as WillStepAnswers;
      return saveAnswers.mutateAsync(merged);
    });
    saveQueue.current = next.catch((err) => {
      lastSaveError.current = err;
    });
    return next;
  };

  const completeTask = async () => {
    if (!estateId) return;
    await saveQueue.current;
    if (lastSaveError.current) throw lastSaveError.current;
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("estate_tasks")
      .update({
        status: "completed",
        completed_at: now,
        last_active_at: now,
      })
      .eq("estate_id", estateId)
      .eq("task_id", TASK_IDS.WILL)
      .not("answers", "is", null)
      .select("id")
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Cannot complete task: answers not persisted");
    queryClient.invalidateQueries({ queryKey: ["willStepAnswers", estateId] });
    queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
  };

  return {
    savedAnswers,
    isFetched,
    saveAnswers: mergeAndSave,
    completeTask,
  };
}
